import { useRef, useState } from 'react';
import { FileUp, Loader2 } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useUploadBlacklistOrDndFile } from '../api';
import type { DndAddTarget } from '../types';

const ALLOWED_EXT = /\.(txt|csv)$/i;
const MAX_BYTES = 1 * 1024 * 1024;

export function BulkBlacklistDndPage() {
  const canAdd = usePermission('Number Action.Manage Bulk Blacklist-DND', BASE_PERMISSION.ADD);
  const { mutate, isPending } = useUploadBlacklistOrDndFile();
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [target, setTarget] = useState<DndAddTarget>('BLACKLIST');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error('Pick a .txt or .csv file to upload.');
      return;
    }
    if (!ALLOWED_EXT.test(file.name)) {
      toast.error('Only .txt or .csv files are supported.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File is larger than 1 MB.');
      return;
    }
    mutate(target, file, {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Uploaded to ${target.toLowerCase()}`);
        if (fileRef.current) fileRef.current.value = '';
        setFileName('');
      },
      onError: () => toast.error('Could not upload the file.'),
    });
  };

  if (!canAdd) return <Navigate to="/voice-blacklist" replace />;

  return (
    <ScrollPage>
      <PageHeader
        title="Bulk Blacklist & DND"
        description="Upload mobile numbers in bulk to either the blacklist or the DND registry."
      />

      <Card className="sm:max-w-lg">
        <CardHeader>
          <CardTitle>Upload file</CardTitle>
          <CardDescription>
            One mobile number per line, .txt or .csv, up to 1 MB.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="dndFile">
                File<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="dndFile"
                ref={fileRef}
                type="file"
                accept=".txt,.csv,text/plain,text/csv"
                onChange={(e) => setFileName(e.target.files?.[0]?.name ?? '')}
              />
              {fileName && (
                <p className="text-xs text-muted-foreground">
                  Selected: <span className="font-mono">{fileName}</span>
                </p>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="bulkTarget">
                Add to<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Select
                value={target}
                onValueChange={(v) => setTarget(v as DndAddTarget)}
              >
                <SelectTrigger id="bulkTarget">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BLACKLIST">Blacklist</SelectItem>
                  <SelectItem value="DND">DND</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileUp className="h-4 w-4" />
                )}
                Upload
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
}
